import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Container,
  Typography,
} from "@mui/material";
import DashboardWidget from "./DashboardWidget";
import {
  onSnapshot,
  query,
} from "firebase/firestore";
import { propertyCollection } from "../utils/firestorePaths";
import PropertyContext from "../contexts/PropertyContext";

export default function FacilitiesDashboard({ property, userInfo }) {
  const [workOrdersLoading, setWorkOrdersLoading] = useState(true);
  const [vendorTasksLoading, setVendorTasksLoading] = useState(true);
  const [maintenanceRequestsLoading, setMaintenanceRequestsLoading] = useState(true);

  const [openWorkOrders, setOpenWorkOrders] = useState([]);
  const [openIssues, setOpenIssues] = useState([]);
  const [maintenanceRequests, setMaintenanceRequests] = useState([]);

  const { selectedProperty } = useContext(PropertyContext);

  // For facilities users, get all properties they have access to
  const userProperties = userInfo?.properties || [];

  const getWorkOrdersForAllProperties = async () => {
    const allWorkOrders = [];
    const unsubscribers = [];

    for (const property of userProperties) {
      const workOrdersRef = propertyCollection(
        property.clientId,
        property.id,
        "workOrders"
      );
      const q = query(workOrdersRef);

      const unsub = onSnapshot(q, (snap) => {
        const propertyOrders = snap.docs
          .map((doc) => ({
            id: doc.id,
            propertyId: property.id,
            propertyName: property.displayName || property.name || property.id,
            ...doc.data(),
          }))
          .filter((request) => request.completed === false)
          .filter((request) => request.markedCompleted !== true);

        // Remove existing orders from this property and add new ones
        const otherPropertyOrders = allWorkOrders.filter(order => order.propertyId !== property.id);
        const updatedOrders = [...otherPropertyOrders, ...propertyOrders];
        setOpenWorkOrders(updatedOrders);
      });
      unsubscribers.push(unsub);
    }
    setWorkOrdersLoading(false);
    return unsubscribers;
  };

  const getPropertyIssuesForAllProperties = async () => {
    const allIssues = [];
    const unsubscribers = [];

    for (const property of userProperties) {
      const issuesRef = propertyCollection(
        property.clientId,
        property.id,
        "propertyIssues"
      );
      const queryIssues = query(issuesRef);

      const unsub = onSnapshot(queryIssues, (snap) => {
        const propertyIssues = snap.docs
          .map((doc) => ({
            id: doc.id,
            propertyId: property.id,
            propertyName: property.displayName || property.name || property.id,
            ...doc.data(),
          }))
          .filter((request) => request.completed === false);

        // Remove existing issues from this property and add new ones
        const otherPropertyIssues = allIssues.filter(issue => issue.propertyId !== property.id);
        const updatedIssues = [...otherPropertyIssues, ...propertyIssues];
        setOpenIssues(updatedIssues);
      });
      unsubscribers.push(unsub);
    }
    setVendorTasksLoading(false);
    return unsubscribers;
  };

  const getMaintenanceRequestsForAllProperties = async () => {
    const allRequests = [];
    const unsubscribers = [];

    for (const property of userProperties) {
      const maintenanceRef = propertyCollection(
        property.clientId,
        property.id,
        "maintenanceRequests"
      );
      const queryMaintenance = query(maintenanceRef);

      const unsub = onSnapshot(queryMaintenance, (snap) => {
        const propertyRequests = snap.docs
          .map((doc) => ({
            id: doc.id,
            propertyId: property.id,
            propertyName: property.displayName || property.name || property.id,
            ...doc.data(),
          }))
          .filter((request) => request.status !== "completed");

        // Remove existing requests from this property and add new ones
        const otherPropertyRequests = allRequests.filter(req => req.propertyId !== property.id);
        const updatedRequests = [...otherPropertyRequests, ...propertyRequests];
        setMaintenanceRequests(updatedRequests);
      });
      unsubscribers.push(unsub);
    }
    setMaintenanceRequestsLoading(false);
    return unsubscribers;
  };

  useEffect(() => {
    if (userProperties.length === 0) return;

    let allUnsubscribers = [];

    const setupSubscriptions = async () => {
      const workOrderUnsubs = await getWorkOrdersForAllProperties();
      const issuesUnsubs = await getPropertyIssuesForAllProperties();
      const maintenanceUnsubs = await getMaintenanceRequestsForAllProperties();

      allUnsubscribers = [...workOrderUnsubs, ...issuesUnsubs, ...maintenanceUnsubs];
    };

    setupSubscriptions();

    return () => {
      allUnsubscribers.forEach(unsub => unsub());
    };
  }, [userProperties]);

  return (
    <Container sx={{ mt: 8 }}>
      <Typography variant="h4" sx={{ mb: 4, textAlign: 'center' }}>
        Facilities Dashboard
      </Typography>

      <Box
        marginBottom="1rem"
        display="flex"
        flexDirection="row"
        width="100%"
        bgColor="background.paper"
        borderRadius="16px"
        border="1"
        borderColor="blue"
      >
        <DashboardWidget
          name={"Work Orders"}
          loading={workOrdersLoading}
          taskCount={openWorkOrders.length}
          desc="Open"
          linkTo="/workOrders"
        />
        <DashboardWidget
          name={"Property Issues"}
          loading={vendorTasksLoading}
          taskCount={openIssues.length}
          linkTo="/propertyIssues"
          desc="Open"
        />
        <DashboardWidget
          name={"Maintenance Requests"}
          loading={maintenanceRequestsLoading}
          taskCount={maintenanceRequests.length}
          desc="Pending"
          linkTo="/maintenanceRequests"
        />
      </Box>

      <Box
        sx={{
          width: "100%",
          borderRadius: "16px",
          border: 1,
          borderColor: "blue",
          p: 3,
          mt: 2,
        }}
      >
        <Typography variant="h6" sx={{ mb: 2 }}>
          Facilities Overview - All Properties
        </Typography>
        <Typography variant="body1">
          Welcome to the Facilities Dashboard. You are viewing aggregated data from all {userProperties.length} properties
          you have access to. This includes all open work orders, property issues, and maintenance requests
          across: {userProperties.map(p => p.displayName || p.name || p.id).join(", ")}. Use the sidebar to navigate
          to specific reports and detailed views.
        </Typography>
      </Box>
    </Container>
  );
}